import {
    Button,
    ButtonType,
    cssX,
    FCX,
    getFormattedThousand,
    sanitize,
    Tooltip,
} from "@datassential/platform-ui-lib";
import cn from "classnames";
import ContentWidget from "@components/ContentWidget/ContentWidget";
import { CSSProperties, useCallback, useMemo, useState } from "react";
import './Header.scss';
import { ProfileDataResponseItem } from "@core/api/useProfileHeader";
import { useCommonDataContext } from "@core/CommonDataContext";
import { useExport } from "@api/useExport";
import ContentWrapper from "@components/ContentWrapper/ContentWrapper";
import MacStageLabel from "@vms/MacStageLabel";
import { InView } from "react-intersection-observer";
import { compareMetrics, PROFILE_HEADER_METRICS_ORDER } from "@models/Metric";
import Title from "@components/Title";
import { useProfileContext } from "@pages/Profile/ProfileContext";
import useModal from "@components/Modal/useModal";
import Modal from "@components/Modal";
import { RiExternalLinkLine } from "react-icons/ri";
import { components } from "@api/api";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";
import { Link } from "react-router-dom";
import { Routes } from "@core/routes";
import { API_URL } from "@core/constants";
import { IoFastFoodOutline } from "react-icons/io5";

export type ProfileExportPptRequest = components['schemas']['FlavorTracker.Entities.DTO.Export.PptExportRequest'];
export type ProfileExportExcelRequest = components['schemas']['FlavorTracker.Entities.DTO.Export.ExcelExportRequest'];

const ProfileHeader: FCX<{
    data: NonNullable<ProfileDataResponseItem>;
    testId?: string;
}> = ({
    data,
    children,
    className,
    testId = "profileHeader",
    ...props
}) => {
    const {
        metrics,
        periodLabel,
    } = useCommonDataContext();

    const {
        amongThose,
        trendMetric,
        categoryId,
    } = useProfileContext();

    const { amongPayload } = useSelectAmongContext();

    const [isImgError, setIsImgError] = useState(false);
    const [isStickyHeaderVisible, setIsStickyHeaderVisible] = useState(false);
    const {
        isOpen: isImageExpanded,
        openModal,
        closeModal,
    } = useModal()

    const {
        id,
        name,
        description,
        thumbnailUrl,
        generalMetrics: generalMetricsValues,
        baseCount,
        penetration,
        macStage: macStageId,
    } = data;

    const pptExportPayload = useMemo(
        (): ProfileExportPptRequest => ({
            ...amongPayload,
            keywordsIds: [id ?? 0],
            amongThose,
            normCategoryId: categoryId,
            trendMetric: trendMetric as ProfileExportPptRequest['trendMetric'],
        }),
        [amongPayload, id, amongThose, categoryId, trendMetric]
    );
    const xlsExportPayload = useMemo(
        (): ProfileExportExcelRequest => ({
            keywordId: id ?? 0,
            normCategoryId: categoryId,
        }),
        [id, categoryId]
    );

    const pptExport = useExport('/api/export/GetPpt', pptExportPayload);
    const xlsExport = useExport('/api/export/GetExcel', xlsExportPayload);

    const isReportDownloading = useMemo(
        () => {
            return pptExport.isLoading || xlsExport.isLoading;
        },
        [pptExport.isLoading, xlsExport.isLoading]
    );

    const generalMetrics = useMemo(
        () => {
            return (generalMetricsValues ?? [])
                .map((i) => ({
                    ...(metrics ?? []).find(o => o.id === i.metric),
                    value: i.raw ?? 0,
                }))
                .sort((a, b) => {
                    return compareMetrics(PROFILE_HEADER_METRICS_ORDER, a.id, b.id);
                });
        },
        [metrics, generalMetricsValues]
    );

    const downloadReport = useCallback(
        (exportType: 'ppt' | 'xls') => {
            if (exportType === 'ppt') {
                pptExport.get();
            }
            if (exportType === 'xls') {
                xlsExport.get();
            }
        },
        [pptExport, xlsExport]
    );

    const exportButtonOptions = useMemo(
        () => (
            <>
                <button
                    className={cssX('Button__option')}
                    data-testid="buttonProfileDownloadPpt"
                    onClick={() => downloadReport('ppt')}
                >
                    PPT
                </button>
                <button
                    className={cssX('Button__option')}
                    data-testid="buttonProfileDownloadXls"
                    onClick={() => downloadReport('xls')}
                >
                    XLSX
                </button>
            </>
        ),
        [downloadReport]
    );

    const exportButtonText = useMemo(
        () => isReportDownloading ? <div className="load-ellipsis">loading</div> : 'Export Results',
        [isReportDownloading]
    );

    const exportButtonProps = useMemo(
        () => ({
            type: ButtonType.HoverOptions,
            isDisabled: isReportDownloading,
            style: {
                pointerEvents: isReportDownloading ? 'none' : 'auto',
            } as CSSProperties,
            hoverContent: exportButtonOptions,
        }),
        [isReportDownloading, exportButtonOptions]
    );

    return (<>
        <ContentWidget
            className={cn(
                "ProfileHeader",
                "Profile__section",
                "appears-top",
                className,
            )}
            testId={testId}
            {...props}
        >
            <div className="ProfileHeader__thumbnail">
                {!isImgError && (
                    <button
                        className="ProfileHeader__thumbnail-image"
                        onClick={openModal}
                        data-testid="buttonProfileHeaderThumbnail"
                    >
                        <img
                            onError={() => setIsImgError(true)}
                            src={thumbnailUrl ?? ''}
                            alt="text"
                            data-testid="imageProfileHeaderThumbnail"
                        />
                    </button>
                )}
                {isImgError && (
                    <div
                        className="ProfileHeader__thumbnail-image is-placeholder"
                        data-testid="buttonProfileHeaderThumbnail"
                    >
                        <IoFastFoodOutline />
                    </div>
                )}
            </div>
            <div className="ProfileHeader__content">
                <div
                    className="ProfileHeader__title"
                    data-testid="textProfileHeaderTitle"
                >
                    {name}
                    <Link
                        className="ProfileHeader__link"
                        to={`${Routes.Correlation}?keywordId=${id}`}
                    >
                        <RiExternalLinkLine
                            data-testid="linkProfileCorrelation"
                            data-tooltip-id="profile-correlate-link"
                        />
                    </Link>
                    <Tooltip className="ProfileHeader__tooltip" id="profile-correlate-link">
                        Find correlated foods and flavors
                    </Tooltip>
                </div>
                <div
                    className="ProfileHeader__description"
                    data-testid="textProfileHeaderDescription"
                >
                    {description}
                </div>
                <div className="ProfileHeader__columns">
                    <div className="ProfileHeader__column">
                        <div
                            className="ProfileHeader__content-title cl-secondary"
                            data-testid="textProfileHeaderConsumer"
                        >CONSUMER
                        </div>
                        <div className="ProfileHeader__sub-columns">
                            <div className="ProfileHeader__sub-column">
                                {generalMetrics.map((metric, idx) => (
                                    <div
                                        key={metric.id}
                                        className="ProfileHeader__row"
                                        data-testid={`textProfileHeaderMetric${idx}`}
                                    >
                                        {metric.value}% {metric.name}
                                    </div>
                                ))}
                            </div>
                            <div className="ProfileHeader__sub-column is-right cl-secondary">
                                <div
                                    className="ProfileHeader__row"
                                    data-testid="textProfileHeaderPeriodLabel"
                                >
                                    {periodLabel}
                                </div>
                                <div
                                    className="ProfileHeader__row"
                                    data-testid="textProfileHeaderBaseCount"
                                >
                                    <b>(n = {getFormattedThousand(baseCount)})</b>
                                </div>
                            </div>
                        </div>
                    </div>
                    {(!!penetration || !!macStageId) && (
                        <div className="ProfileHeader__column">
                            <div
                                className="ProfileHeader__content-title cl-secondary"
                                data-testid="textProfileHeaderMenu"
                            >
                                MENU
                            </div>
                            {!!penetration && (
                                <div
                                    className="ProfileHeader__row"
                                    data-testid="textProfileHeaderPenetration"
                                >
                                    found on {penetration?.toFixed(1)}% of restaurant menus
                                </div>
                            )}
                            {!!macStageId && (
                                <div className="ProfileHeader__row">
                                    <span data-testid="textProfileHeaderMacStage">MAC stage&nbsp;&nbsp;</span>
                                    <MacStageLabel
                                        macStageId={macStageId}
                                        testId="textProfileHeaderMacStage"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="ProfileHeader__download">
                <Button
                    {...exportButtonProps}
                    className="ProfileHeader__download-button"
                    testId="buttonProfileHeaderExport"
                >
                    {exportButtonText}
                </Button>
            </div>
            <InView
                as="div"
                className="ProfileHeader__sticky-trigger"
                onChange={(inView) => setIsStickyHeaderVisible(!inView)}
            />
        </ContentWidget>
        {isStickyHeaderVisible && (
            <div className="StickyProfileHeader appears-bottom">
                <ContentWrapper>
                    <div className="StickyProfileHeader__content">
                        <div className="StickyProfileHeader__left">
                            <Title testId="textProfileHeaderTitleSticky">
                                {name?.toUpperCase() ?? ''}
                            </Title>
                            <div
                                className="cl-secondary"
                                data-testid="textProfileHeaderBaseCountSticky"
                            >
                                <b>(n = {getFormattedThousand(baseCount)})</b>
                            </div>
                            {!!macStageId && (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span data-testid="textProfileHeaderMacStageSticky">MAC Stage&nbsp;&nbsp;</span>
                                    <MacStageLabel
                                        macStageId={macStageId}
                                        modifiers={['small']}
                                        testId="textProfileHeaderMacStageSticky"
                                    />
                                </div>
                            )}
                        </div>
                        <Button
                            {...exportButtonProps}
                            className="StickyProfileHeader__download-button"
                            testId="buttonProfileHeaderExportSticky"
                        >
                            {exportButtonText}
                        </Button>
                    </div>
                </ContentWrapper>
            </div>
        )}
        {isImageExpanded && (
            <Modal onClose={closeModal}>
                <img
                    alt="test"
                    className="ProfileHeader__image-expanded"
                    src={sanitize(`${API_URL}/api/image/get/${id}`)}
                />
            </Modal>
        )}
    </>);
};

export default ProfileHeader;